<template>
  <div class="placement-activity-wrapper">
    <div class="placement-activity-filter">
      <form id="placementActivityOptions">
        <div class="inline-group">
          <label>Start Date</label>
          <form-date-picker
            v-model="placementActivityStartDate"
            type="bordered"
            @change="onStartDateRangeSelect"
          />
        </div>
        <div class="inline-group">
          <label>End Date</label>
          <form-date-picker
            type="bordered"
            :value="placementActivityEndDate"
            @change="onEndDateRangeSelect"
          />
        </div>

        <div class="inline-group">
          <label>Select Branch:</label>
          <dropdown-select
            class="bordered"
            :menu-items="branchNames"
            :selected-item="defaultInputFields.branchName"
            @select-item="onSelectBranch($event)"
          />
        </div>

        <div class="inline-group">
          <label>Select Division:</label>
          <dropdown-select
            class="bordered"
            :menu-items="divisionAbbrs"
            :selected-item="defaultInputFields.divisionAbbr"
            @select-item="onSelectDivision($event)"
          />
        </div>
        <div class="inline-group">
          <label>Client</label>
          <tol-type-ahead
            :search-params="{
              url: '/assignments/user-quick-search',
              searchKeys: ['name'],
              objectType: 'client'
            }"
            class="bordered"
            :menu-label="selectedClientName"
            @select-item="onSelectClient($event)"
          >
            <template v-slot="{ value }">
              {{ value.name }}
            </template>
          </tol-type-ahead>
        </div>
        <div class="inline-group">
          <label>Referral?</label>
          <dropdown-select
            class="bordered"
            :menu-items="referralValues"
            :selected-item="referralValues[0]"
            @select-item="onSelectReferral($event)"
          />
        </div>

        <div class="inline-group">
          <label>Select Entity:</label>
          <dropdown-select
            class="generate-entity-dropdown dropdown bordered"
            :menu-items="entityNames"
            :selected-item="entityNames[0]"
            @select-item="onSelectEntity($event)"
          />
        </div>
        <div class="inline-group btn">
          <label></label>
          <tol-button
            id="save-btn"
            button-type="save"
            label="Update Dashboard"
            class="module-action"
            @click="updatePlacementActivityDashboard()"
          />
        </div>
      </form>
    </div>

    <br />
    <vue-simple-spinner v-if="isPlacementActivityLoading" size="massive" />
    <div v-else class="placement-activity-results">
      <PlacementActivityResults />
    </div>
    <br />
    <div class="placement-activity-table">
      <PlacementActivityTable />
    </div>
  </div>
</template>
<script>
import DropdownSelect from '@/app/components/DropdownSelect';
import FormDatePicker from '@/app/components/FormDatePicker';
import PlacementActivityResults from './components/PlacementActivityResults.vue';
import { AppGetters } from '@/app/app.keys';
import { PlacementActivityGetters, PlacementActivityActions } from './placement-activity.keys';
import format from 'date-fns/format';
import sub_months from 'date-fns/sub_months';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import { dateUtils } from '@/mixins/date-utils';
import TolButton from '@/app/components/TolButton.vue';
import { AppActions } from '@/app/app.keys';
import PlacementActivityTable from './components/PlacementActivityTable.vue';

export default {
  name: 'PlacementActivity',
  components: {
    PlacementActivityTable,
    DropdownSelect,
    FormDatePicker,
    PlacementActivityResults,
    TolTypeAhead,
    TolButton
  },
  mixins: [dateUtils],
  data() {
    return {
      defaultInputFields: {
        rangeStart: sub_months(new Date(), 3),
        rangeEnd: new Date(),
        branchName: 'All',
        divisionAbbr: 'All',
        referral: 'Both',
        clientId: '',
        entity: 'All'
      },
      selectedClientName: '',
      referralValues: ['Both', 'Yes', 'No']
    };
  },
  computed: {
    placementActivityList() {
      return this.$store.getters[PlacementActivityGetters.PlacementActivityList];
    },
    isPlacementActivityLoading() {
      return this.$store.getters[PlacementActivityGetters.IsPlacementActivityLoading];
    },
    branchNames() {
      return ['All', ...this.$store.getters[AppGetters.BranchNames]];
    },
    entityNames() {
      return ['All', ...this.$store.getters[AppGetters.EntityTypes]];
    },
    divisionAbbrs() {
      let divAbbrs = this.$store.getters[AppGetters.DivisionAbbrs].map(div => div.abbr);
      return ['All', ...divAbbrs];
    },
    placementActivityStartDate() {
      return this.defaultInputFields.rangeStart ? this.defaultInputFields.rangeStart : null;
    },
    placementActivityEndDate() {
      return this.defaultInputFields.rangeEnd ? this.defaultInputFields.rangeEnd : null;
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('placementActivity.title'));
    this.updatePlacementActivityDashboard();
  },
  methods: {
    updatePlacementActivityDashboard() {
      this.$store.dispatch(
        PlacementActivityActions.SetPlacementActivityInputFields,
        this.defaultInputFields
      );
      this.$store.dispatch(PlacementActivityActions.RetrievePlacementActivityList);
      this.$store.dispatch(PlacementActivityActions.RetrievePlacementActivityCount);
    },
    formatDate(date) {
      return date ? format(date, 'YYYY-MM-DD') : null;
    },
    onStartDateRangeSelect(event) {
      this.defaultInputFields.rangeStart = event;
    },
    onEndDateRangeSelect(event) {
      this.defaultInputFields.rangeEnd = event;
    },
    onSelectBranch(event) {
      this.defaultInputFields.branchName = event;
    },
    onSelectDivision(evt) {
      this.defaultInputFields.divisionAbbr = evt;
    },
    onSelectClient(event) {
      this.selectedClientName = event.name;
      this.defaultInputFields.clientId = event.reference_number;
    },
    onSelectReferral(event) {
      this.defaultInputFields.referral = event;
    },
    onSelectEntity(event) {
      this.defaultInputFields.entity = event;
    }
  }
};
</script>
<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.placement-activity-filter {
  background-color: $background-level-2;
  padding: 2rem;
  margin: 2rem;
}

form {
  display: grid;
  grid-template-columns: 1fr;
}

.inline-group {
  margin-bottom: 15px;

  &.btn {
    display: flex;

    align-items: flex-end;
  }
}

.placement-activity-wrapper {
  display: flex;
  flex-direction: column;
  background-color: $background-level-3;
}
.update-dashboard-button {
  width: 15rem;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
}

.tol-button {
  height: 3rem;
  background-color: $blue;
  display: flex;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

#save-btn {
  width: 100%;
}

@media only screen and (min-width: 768px) {
  form {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }
  .inline-group.btn {
    grid-column: 3;
  }
}
</style>
